import React, { useMemo, useState } from 'react';
import { Bot, Mic, FileText, Compass, Table2, CheckCircle2 } from 'lucide-react';
const KEYWORDS = new Set([
    'const',
    'let',
    'var',
    'function',
    'return',
    'async',
    'await',
    'if',
    'else',
    'for',
    'while',
    'switch',
    'case',
    'break',
    'continue',
    'try',
    'catch',
    'finally',
    'throw',
    'new',
    'class',
    'extends',
    'import',
    'from',
    'export',
    'default',
    'type',
    'interface',
    'as',
    'true',
    'false',
    'null',
    'undefined',
]);
function tokenizeLine(line, state) {
    const tokens = [];
    let i = 0;
    const push = (kind, text) => {
        if (!text)
            return;
        tokens.push({ kind, text });
    };
    while (i < line.length) {
        const rest = line.slice(i);
        // Block comment continuation
        if (state.inBlockComment) {
            const endIdx = rest.indexOf('*/');
            if (endIdx === -1) {
                push('comment', rest);
                i = line.length;
                continue;
            }
            push('comment', rest.slice(0, endIdx + 2));
            i += endIdx + 2;
            state.inBlockComment = false;
            continue;
        }
        // Line comment
        if (rest.startsWith('//')) {
            push('comment', rest);
            break;
        }
        // Block comment start
        if (rest.startsWith('/*')) {
            const endIdx = rest.indexOf('*/', 2);
            if (endIdx === -1) {
                push('comment', rest);
                state.inBlockComment = true;
                break;
            }
            push('comment', rest.slice(0, endIdx + 2));
            i += endIdx + 2;
            continue;
        }
        // Strings (single/double/backtick)
        const ch = line[i];
        if (ch === '"' || ch === "'" || ch === '`') {
            const quote = ch;
            let j = i + 1;
            let escaped = false;
            while (j < line.length) {
                const c = line[j];
                if (escaped) {
                    escaped = false;
                    j += 1;
                    continue;
                }
                if (c === '\\') {
                    escaped = true;
                    j += 1;
                    continue;
                }
                if (c === quote) {
                    j += 1;
                    break;
                }
                j += 1;
            }
            push('string', line.slice(i, j));
            i = j;
            continue;
        }
        // JSX-ish tags: <Tag or </Tag
        if (ch === '<') {
            const m = rest.match(/^<\s*\/?\s*[A-Za-z][A-Za-z0-9_-]*/);
            if (m?.[0]) {
                push('tag', m[0]);
                i += m[0].length;
                continue;
            }
            push('punct', '<');
            i += 1;
            continue;
        }
        // Numbers
        const num = rest.match(/^\d+(\.\d+)?/);
        if (num?.[0]) {
            push('number', num[0]);
            i += num[0].length;
            continue;
        }
        // Identifiers / keywords
        const ident = rest.match(/^[A-Za-z_$][A-Za-z0-9_$]*/);
        if (ident?.[0]) {
            const t = ident[0];
            push(KEYWORDS.has(t) ? 'keyword' : 'text', t);
            i += t.length;
            continue;
        }
        // Punctuation / whitespace
        const p = rest.match(/^[^\w]+/);
        if (p?.[0]) {
            const punct = p[0];
            // keep whitespace as plain text so it preserves indentation
            const kind = punct.trim().length === 0 ? 'text' : 'punct';
            push(kind, punct);
            i += punct.length;
            continue;
        }
        // fallback (shouldn't happen)
        push('text', line[i]);
        i += 1;
    }
    return tokens;
}
function StandaloneCodeBlock({ code }) {
    const lines = code.replace(/\r\n/g, '\n').split('\n');
    const state = { inBlockComment: false };
    const clsFor = (kind) => {
        switch (kind) {
            case 'keyword':
                return 'text-violet-300';
            case 'string':
                return 'text-emerald-300';
            case 'comment':
                return 'text-slate-500 italic';
            case 'number':
                return 'text-amber-300';
            case 'tag':
                return 'text-sky-300';
            case 'punct':
                return 'text-slate-300';
            default:
                return 'text-slate-100';
        }
    };
    return (React.createElement("pre", { className: "w-full overflow-x-auto rounded-xl border border-slate-800 bg-[#0b1220] text-[13px] leading-relaxed" },
        React.createElement("code", { className: "block p-4 font-mono" }, lines.map((line, idx) => {
            const tokens = tokenizeLine(line, state);
            return (React.createElement("div", { key: idx, className: "whitespace-pre" }, tokens.length === 0 ? (React.createElement("span", { className: "text-slate-100" }, '')) : (tokens.map((t, j) => (React.createElement("span", { key: j, className: clsFor(t.kind) }, t.text))))));
        }))));
}
function MicCapture({ title, hint, transcript, interpreted, chips, }) {
    return (React.createElement("div", { className: "rounded-xl border border-slate-200 bg-white p-3" },
        React.createElement("div", { className: "flex items-start justify-between gap-3" },
            React.createElement("div", { className: "min-w-0" },
                React.createElement("div", { className: "text-xs font-semibold text-slate-900" }, title),
                React.createElement("div", { className: "text-[11px] text-slate-500" }, hint)),
            React.createElement("div", { className: "flex items-center gap-2" },
                React.createElement("div", { className: "flex items-end gap-1 h-6" },
                    React.createElement("span", { className: "w-1 rounded bg-emerald-500/80", style: { height: '35%' } }),
                    React.createElement("span", { className: "w-1 rounded bg-emerald-500/80", style: { height: '70%' } }),
                    React.createElement("span", { className: "w-1 rounded bg-emerald-500/80", style: { height: '55%' } }),
                    React.createElement("span", { className: "w-1 rounded bg-emerald-500/80", style: { height: '85%' } }),
                    React.createElement("span", { className: "w-1 rounded bg-emerald-500/80", style: { height: '45%' } })),
                React.createElement("button", { className: "h-9 w-9 rounded-full bg-slate-900 text-white flex items-center justify-center" },
                    React.createElement(Mic, { className: "h-4.5 w-4.5" })))),
        React.createElement("div", { className: "mt-3 space-y-2" },
            React.createElement("div", { className: "rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800" },
                React.createElement("span", { className: "text-[11px] text-slate-500 mr-2" }, "Heard:"),
                "\u201C",
                transcript,
                "\u201D"),
            interpreted ? (React.createElement("div", { className: "rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" },
                React.createElement("span", { className: "font-semibold" }, "Interpreted:"),
                " ",
                interpreted)) : null,
            chips && chips.length ? (React.createElement("div", { className: "flex flex-wrap gap-2" }, chips.map((c) => (React.createElement("span", { key: `${c.label}:${c.value}`, className: "text-[11px] px-2 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-700" },
                c.label,
                ": ",
                React.createElement("span", { className: "font-mono" }, c.value)))))) : null)));
}
function VoiceLayerFrame({ children }) {
    return (React.createElement("div", { className: "relative rounded-2xl border border-dashed border-slate-300 bg-slate-100/80 p-3" },
        React.createElement("div", { className: "absolute right-3 top-3 text-[10px] font-semibold tracking-widest text-slate-500 bg-white/80 border border-slate-200 px-2 py-0.5 rounded-full" }, "VOICE"),
        children));
}
const SNIPPETS = {
    chatActions: {
        title: 'AI Chat Actions',
        description: 'Let users ask, then run tools to do real work.',
        language: 'tsx',
        code: `// Define your tools
const tools = [
  {
    name: 'createVendor',
    description: 'Creates a new vendor',
    parameters: z.object({
      name: z.string(),
      category: z.string(),
    }),
    execute: async ({ name, category }) => {
      await api.vendors.create({ name, category });
      return \`Created vendor \${name}\`;
    },
  },
];

// Render the component
<FluidChat tools={tools} />`,
    },
    voiceCommands: {
        title: 'Voice Commands',
        description: 'Turn speech into structured commands.',
        language: 'tsx',
        code: `<FluidVoice
  onCommand={async (cmd) => {
    if (cmd.intent === 'run_report') {
      await generateReport(cmd.params);
    }
  }}
  trigger="click-to-speak"
  feedback="visual"
/>`,
    },
    intelligentForms: {
        title: 'Intelligent Forms',
        description: 'Accept voice, paste, and uploads — then autofill validated data.',
        language: 'tsx',
        code: `<FluidForm
  schema={vendorSchema}
  sources={['upload', 'voice', 'paste']}
  autoFill={true}
  onComplete={(data) => {
    // Data is fully validated and structured
    console.log(data);
  }}
/>`,
    },
    smartNavigation: {
        title: 'Smart Navigation',
        description: 'Ask for what you need and jump to the right screen.',
        language: 'tsx',
        code: `// Natural language navigation
const { goTo } = useSmartNavigation();

await goTo("Show me all active projects in finance");
// -> routes to /projects?status=active&dept=finance`,
    },
    aiTablesCharts: {
        title: 'AI Tables & Charts',
        description: 'Make your data interactive with AI-assisted exploration.',
        language: 'tsx',
        code: `<AITables
  data={rows}
  onAsk={async (question) => {
    return await analyze(rows, question);
  }}
/>

<AICharts data={rows} />`,
    },
};
function FeatureCard({ title, description, icon, active, onClick, }) {
    return (React.createElement("button", { type: "button", onClick: onClick, className: [
            'w-full text-left rounded-xl border px-4 py-3 transition-colors',
            active ? 'border-orange-300 bg-orange-50' : 'border-slate-200 bg-white hover:bg-slate-50',
        ].join(' ') },
        React.createElement("div", { className: "flex items-start gap-3" },
            React.createElement("div", { className: [
                    'mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg border',
                    active ? 'border-orange-200 bg-white text-orange-700' : 'border-slate-200 bg-slate-50 text-slate-700',
                ].join(' ') }, icon),
            React.createElement("div", { className: "min-w-0" },
                React.createElement("div", { className: "flex items-center gap-2" },
                    React.createElement("div", { className: "font-semibold text-slate-900" }, title),
                    active ? React.createElement(CheckCircle2, { className: "h-4 w-4 text-orange-600" }) : null),
                React.createElement("div", { className: "text-sm text-slate-600 leading-snug" }, description)))));
}
function ChatActionsPreview() {
    return (React.createElement("div", { className: "space-y-3" },
        React.createElement("div", { className: "rounded-xl border border-slate-200 bg-white p-3" },
            React.createElement("div", { className: "text-xs font-semibold text-slate-700 mb-2" }, "AI Chat"),
            React.createElement("div", { className: "space-y-2" },
                React.createElement("div", { className: "max-w-[85%] rounded-2xl bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-800" }, "Create a vendor called \u201CAcme Supplies\u201D in category \u201COffice\u201D."),
                React.createElement("div", { className: "ml-auto max-w-[85%] rounded-2xl bg-slate-900 px-3 py-2 text-sm text-white" }, "Done. I created \u201CAcme Supplies\u201D."))),
        React.createElement("div", { className: "text-xs text-slate-600" }, "Chat triggers tools, tools update UI automatically.")));
}
function VoiceCommandsPreview() {
    return (React.createElement("div", { className: "space-y-3" },
        React.createElement("div", { className: "flex items-center gap-3" },
            React.createElement("button", { className: "h-10 w-10 rounded-full bg-slate-900 text-white flex items-center justify-center" },
                React.createElement(Mic, { className: "h-5 w-5" })),
            React.createElement("div", null,
                React.createElement("div", { className: "text-sm font-semibold text-slate-900" }, "Listening\u2026"),
                React.createElement("div", { className: "text-xs text-slate-600" }, "Say \u201Crun report for last month\u201D."))),
        React.createElement("div", { className: "rounded-xl border border-slate-200 bg-white p-3" },
            React.createElement("div", { className: "text-xs font-semibold text-slate-700 mb-2" }, "Recognized command"),
            React.createElement("div", { className: "flex flex-wrap gap-2" },
                React.createElement("span", { className: "text-[11px] px-2 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-700" },
                    "intent: ",
                    React.createElement("span", { className: "font-mono" }, "run_report")),
                React.createElement("span", { className: "text-[11px] px-2 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-700" },
                    "range: ",
                    React.createElement("span", { className: "font-mono" }, "last_month"))))));
}
function IntelligentFormsPreview() {
    return (React.createElement("div", { className: "space-y-3" },
        React.createElement("div", { className: "text-sm font-semibold text-slate-900" }, "Speak \u2192 form fills in"),
        React.createElement("div", { className: "grid grid-cols-1 lg:grid-cols-12 gap-3 items-start" },
            React.createElement("div", { className: "lg:col-span-5 space-y-3" },
                React.createElement(VoiceLayerFrame, null,
                    React.createElement("div", { className: "space-y-3" },
                        React.createElement(MicCapture, { title: "Voice input", hint: "Talk naturally \u2014 we extract structured fields.", transcript: "The vendor is Acme Supplies, category Office.", interpreted: "name + category extracted", chips: [
                                { label: 'name', value: 'Acme Supplies' },
                                { label: 'category', value: 'Office' },
                            ] }),
                        React.createElement("div", { className: "text-[11px] text-slate-500" }, "This is the voice layer (input). It drives updates into the real UI on the right.")))),
            React.createElement("div", { className: "lg:col-span-7" },
                React.createElement("div", { className: "rounded-xl border border-slate-200 bg-white p-3" },
                    React.createElement("div", { className: "flex items-center justify-between mb-3" },
                        React.createElement("div", { className: "text-xs font-semibold text-slate-900" }, "Vendor onboarding"),
                        React.createElement("div", { className: "flex gap-2" },
                            React.createElement("span", { className: "text-[11px] px-2 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-700" }, "voice"),
                            React.createElement("span", { className: "text-[11px] px-2 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-700" }, "upload"),
                            React.createElement("span", { className: "text-[11px] px-2 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-700" }, "paste"))),
                    React.createElement("div", { className: "space-y-2" },
                        React.createElement("div", null,
                            React.createElement("div", { className: "text-xs text-slate-600 mb-1" }, "Vendor name"),
                            React.createElement("div", { className: "h-10 rounded-lg border border-emerald-200 bg-emerald-50 px-3 flex items-center justify-between text-sm text-slate-800" },
                                React.createElement("span", null, "Acme Supplies"),
                                React.createElement("span", { className: "text-[11px] text-emerald-700 font-medium" }, "autofilled"))),
                        React.createElement("div", null,
                            React.createElement("div", { className: "text-xs text-slate-600 mb-1" }, "Category"),
                            React.createElement("div", { className: "h-10 rounded-lg border border-emerald-200 bg-emerald-50 px-3 flex items-center justify-between text-sm text-slate-800" },
                                React.createElement("span", null, "Office"),
                                React.createElement("span", { className: "text-[11px] text-emerald-700 font-medium" }, "autofilled"))),
                        React.createElement("div", null,
                            React.createElement("div", { className: "text-xs text-slate-600 mb-1" }, "Tax ID"),
                            React.createElement("div", { className: "h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 flex items-center text-sm text-slate-500" }, "Ask a follow-up if missing\u2026"))))))));
}
function SmartNavigationPreview() {
    return (React.createElement("div", { className: "space-y-3" },
        React.createElement("div", { className: "rounded-xl border border-slate-200 bg-white px-3 py-2" },
            React.createElement("div", { className: "text-[11px] text-slate-500 mb-1" }, "Ask to navigate"),
            React.createElement("div", { className: "text-sm text-slate-900" }, "\u201CShow me all active projects in finance\u201D")),
        React.createElement("div", { className: "rounded-xl border border-slate-200 bg-slate-50 p-3" },
            React.createElement("div", { className: "text-xs font-semibold text-slate-700 mb-2" }, "Suggested destinations"),
            React.createElement("ul", { className: "space-y-2 text-sm" },
                React.createElement("li", { className: "flex items-center justify-between rounded-lg bg-white border border-slate-200 px-3 py-2" },
                    React.createElement("span", { className: "text-slate-900" }, "Projects"),
                    React.createElement("span", { className: "font-mono text-xs text-slate-600" }, "/projects?status=active&dept=finance")),
                React.createElement("li", { className: "flex items-center justify-between rounded-lg bg-white border border-slate-200 px-3 py-2" },
                    React.createElement("span", { className: "text-slate-900" }, "Finance dashboard"),
                    React.createElement("span", { className: "font-mono text-xs text-slate-600" }, "/dashboards/finance"))))));
}
function AITablesChartsPreview() {
    return (React.createElement("div", { className: "grid grid-cols-1 lg:grid-cols-12 gap-4 items-start" },
        React.createElement("div", { className: "lg:col-span-4 space-y-3" },
            React.createElement(VoiceLayerFrame, null,
                React.createElement("div", { className: "space-y-3" },
                    React.createElement(MicCapture, { title: "Voice question", hint: "Ask about your data \u2014 it turns into filters and actions.", transcript: "Show revenue by month and highlight December.", interpreted: "groupBy=month \u2022 highlight=Dec", chips: [
                            { label: 'groupBy', value: 'month' },
                            { label: 'highlight', value: 'Dec' },
                        ] }),
                    React.createElement("div", { className: "rounded-xl border border-slate-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" }, "\u201CDec stands out \u2014 largest month-over-month increase.\u201D")))),
        React.createElement("div", { className: "lg:col-span-8 grid grid-cols-1 lg:grid-cols-2 gap-4 items-start" },
            React.createElement("div", { className: "rounded-xl border border-slate-200 bg-white overflow-hidden" },
                React.createElement("div", { className: "px-3 py-2 border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-700 flex items-center justify-between" },
                    React.createElement("span", null, "Revenue table"),
                    React.createElement("span", { className: "text-[11px] px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-700" }, "grouped: month")),
                React.createElement("div", { className: "p-3" },
                    React.createElement("table", { className: "w-full text-xs" },
                        React.createElement("thead", null,
                            React.createElement("tr", { className: "text-slate-500" },
                                React.createElement("th", { className: "text-left font-semibold py-1" }, "Month"),
                                React.createElement("th", { className: "text-left font-semibold py-1" }, "Revenue"),
                                React.createElement("th", { className: "text-left font-semibold py-1" }, "Change"))),
                        React.createElement("tbody", { className: "text-slate-800" },
                            React.createElement("tr", { className: "border-b border-slate-100" },
                                React.createElement("td", { className: "py-1" }, "Oct"),
                                React.createElement("td", { className: "py-1" }, "$42k"),
                                React.createElement("td", { className: "py-1" }, "+8%")),
                            React.createElement("tr", { className: "border-b border-slate-100" },
                                React.createElement("td", { className: "py-1" }, "Nov"),
                                React.createElement("td", { className: "py-1" }, "$47k"),
                                React.createElement("td", { className: "py-1" }, "+12%")),
                            React.createElement("tr", { className: "bg-orange-50 border border-orange-200" },
                                React.createElement("td", { className: "py-1 font-semibold text-slate-900" }, "Dec"),
                                React.createElement("td", { className: "py-1 font-semibold text-slate-900" }, "$51k"),
                                React.createElement("td", { className: "py-1 font-semibold text-slate-900" }, "+9%")))))),
            React.createElement("div", { className: "rounded-xl border border-slate-200 bg-slate-50 p-3" },
                React.createElement("div", { className: "text-xs font-semibold text-slate-700 mb-2 flex items-center justify-between" },
                    React.createElement("span", null, "Mini chart"),
                    React.createElement("span", { className: "text-[11px] px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-700" }, "highlight: Dec")),
                React.createElement("div", { className: "flex items-end gap-2 h-28" },
                    React.createElement("div", { className: "w-8 bg-slate-300 rounded-t", style: { height: '45%' } }),
                    React.createElement("div", { className: "w-8 bg-slate-400 rounded-t", style: { height: '60%' } }),
                    React.createElement("div", { className: "w-8 bg-slate-500 rounded-t", style: { height: '75%' } }),
                    React.createElement("div", { className: "w-8 bg-orange-500 rounded-t ring-2 ring-orange-300", style: { height: '85%' } })),
                React.createElement("div", { className: "mt-3 text-xs text-slate-600" },
                    "Voice \u2192 interpretation \u2192 ",
                    React.createElement("span", { className: "font-mono" }, "filter/sort/highlight"),
                    " updates the UI.")))));
}
export function LandingSandboxPage() {
    const features = useMemo(() => [
        { key: 'chatActions', icon: React.createElement(Bot, { className: "h-5 w-5" }) },
        { key: 'voiceCommands', icon: React.createElement(Mic, { className: "h-5 w-5" }) },
        { key: 'intelligentForms', icon: React.createElement(FileText, { className: "h-5 w-5" }) },
        { key: 'smartNavigation', icon: React.createElement(Compass, { className: "h-5 w-5" }) },
        { key: 'aiTablesCharts', icon: React.createElement(Table2, { className: "h-5 w-5" }) },
    ], []);
    const [active, setActive] = useState('chatActions');
    const snippet = SNIPPETS[active];
    const previewByKey = useMemo(() => ({
        chatActions: React.createElement(ChatActionsPreview, null),
        voiceCommands: React.createElement(VoiceCommandsPreview, null),
        intelligentForms: React.createElement(IntelligentFormsPreview, null),
        smartNavigation: React.createElement(SmartNavigationPreview, null),
        aiTablesCharts: React.createElement(AITablesChartsPreview, null),
    }), []);
    return (React.createElement("div", { className: "space-y-6" },
        React.createElement("header", { className: "space-y-1" },
            React.createElement("h1", { className: "text-xl font-semibold text-gray-900" }, "Landing Sandbox"),
            React.createElement("p", { className: "text-gray-600 text-sm" }, "Highlighted snippets + a matching visual preview (for marketing/landing pages).")),
        React.createElement("section", { className: "grid grid-cols-1 lg:grid-cols-12 gap-5 items-start" },
            React.createElement("div", { className: "lg:col-span-4 space-y-3" }, features.map((f) => {
                const cfg = SNIPPETS[f.key];
                return (React.createElement(FeatureCard, { key: f.key, title: cfg.title, description: cfg.description, icon: f.icon, active: active === f.key, onClick: () => setActive(f.key) }));
            })),
            React.createElement("div", { className: "lg:col-span-8" },
                React.createElement("div", { className: "rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" },
                    React.createElement("div", { className: "flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50" },
                        React.createElement("div", { className: "min-w-0" },
                            React.createElement("div", { className: "font-semibold text-slate-900 truncate" }, snippet.title),
                            React.createElement("div", { className: "text-xs text-slate-600 truncate" }, snippet.description)),
                        React.createElement("div", { className: "text-xs text-slate-500" }, "Example.tsx")),
                    React.createElement("div", { className: "p-4" },
                        React.createElement("div", { className: "space-y-3" },
                            React.createElement(StandaloneCodeBlock, { code: snippet.code }),
                            React.createElement("div", { className: "rounded-xl border border-slate-200 bg-slate-50 p-4" }, previewByKey[active]))))))));
}
export default LandingSandboxPage;
